import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Lock, RefreshCw, CheckCircle, Clock, DollarSign, Users, LogOut, Database, AlertCircle, Briefcase, Tag, Edit2, Save, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [qbStatus, setQbStatus] = useState(null);
  const [applications, setApplications] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [editingPricing, setEditingPricing] = useState(false);
  const [pricingDraft, setPricingDraft] = useState(null);
  const [activeTab, setActiveTab] = useState('bookings');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState({});

  useEffect(() => {
    const storedPassword = sessionStorage.getItem('admin_password');
    if (storedPassword) {
      setPassword(storedPassword);
      authenticate(storedPassword);
    }
  }, []);

  const loadData = useCallback(async (pwd) => {
    setLoading(true);
    try {
      const headers = { 'X-Admin-Password': pwd };
      const [bookingsRes, statsRes, qbRes, appsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/quickbooks/status`, { headers }),
        axios.get(`${API}/admin/job-applications`, { headers }),
        axios.get(`${API}/admin/pricing`, { headers })
      ]);
      setBookings(bookingsRes.data.bookings);
      setStats(statsRes.data);
      setQbStatus(qbRes.data);
      setApplications(appsRes.data.applications);
      setPricing(pricingRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  const authenticate = useCallback(async (pwd) => {
    try {
      const response = await axios.post(`${API}/admin/auth`, { password: pwd });
      if (response.data.success) {
        setAuthenticated(true);
        sessionStorage.setItem('admin_password', pwd);
        await loadData(pwd);
      }
    } catch (error) {
      toast.error('Invalid password');
      sessionStorage.removeItem('admin_password');
    }
  }, [loadData]);

  const startEditPricing = () => {
    setPricingDraft(JSON.parse(JSON.stringify(pricing)));
    setEditingPricing(true);
  };

  const cancelEditPricing = () => {
    setEditingPricing(false);
    setPricingDraft(null);
  };

  const savePricing = async () => {
    try {
      await axios.put(`${API}/admin/pricing`, {
        service_base_prices: pricingDraft.service_base_prices,
        extra_services_prices: pricingDraft.extra_services_prices,
        room_prices: pricingDraft.room_prices
      }, { headers: { 'X-Admin-Password': password } });
      toast.success('Pricing updated successfully');
      setEditingPricing(false);
      setPricingDraft(null);
      await loadData(password);
    } catch (error) {
      toast.error('Failed to save pricing');
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    authenticate(password);
  };

  const handleLogout = () => {
    setAuthenticated(false);
    setPassword('');
    sessionStorage.removeItem('admin_password');
  };

  const handleSyncBooking = async (bookingId) => {
    setSyncing({ ...syncing, [bookingId]: true });
    try {
      await axios.post(
        `${API}/admin/quickbooks/sync-booking`,
        { booking_id: bookingId },
        { headers: { 'X-Admin-Password': password } }
      );
      toast.success('Booking synced to QuickBooks!');
      await loadData(password);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync booking');
    } finally {
      setSyncing({ ...syncing, [bookingId]: false });
    }
  };

  const handleConnectQuickBooks = async () => {
    try {
      const response = await axios.get(`${API}/admin/quickbooks/authorize`, {
        headers: { 'X-Admin-Password': password }
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get authorization URL');
    }
  };

  const handleDisconnectQuickBooks = async () => {
    if (!window.confirm('Disconnect from QuickBooks?')) return;
    try {
      await axios.post(`${API}/admin/quickbooks/disconnect`, {}, {
        headers: { 'X-Admin-Password': password }
      });
      toast.success('Disconnected from QuickBooks');
      await loadData(password);
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const qbCallbackStatus = urlParams.get('qb');
    const reason = urlParams.get('reason');
    if (qbCallbackStatus === 'connected') {
      toast.success('QuickBooks connected successfully!');
      window.history.replaceState({}, '', '/admin');
    } else if (qbCallbackStatus === 'error') {
      let msg;
      if (reason === 'invalid_scope') {
        msg = 'QuickBooks rejected the Accounting scope. Enable "Accounting" API in your Intuit app settings.';
      } else if (reason === 'missing_params') {
        msg = 'QuickBooks did not return an auth code. Please try again.';
      } else {
        msg = `QuickBooks connection failed${reason ? ': ' + reason : ''}. Please try again.`;
      }
      toast.error(msg, { duration: 8000 });
      window.history.replaceState({}, '', '/admin');
    }
  }, []);

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FDFDFC' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-12 max-w-md w-full mx-8"
          style={{ border: '1px solid #E2E8F0' }}
        >
          <div className="text-center mb-8">
            <Lock className="w-12 h-12 mx-auto mb-4" style={{ color: '#0284C7' }} />
            <h1 className="text-3xl font-medium mb-2" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
              Admin Dashboard
            </h1>
            <p className="text-base" style={{ color: '#475569' }}>
              Enter password to access
            </p>
          </div>
          <form onSubmit={handleLogin}>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl mb-4"
              style={{ border: '1px solid #E2E8F0' }}
              placeholder="Password"
              autoFocus
            />
            <button
              data-testid="admin-login-btn"
              type="submit"
              className="w-full px-8 py-3 rounded-full font-medium hover:opacity-90 transition-opacity"
              style={{ background: '#0284C7', color: '#FFFFFF' }}
            >
              Login
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F3F4F6' }}>
      <header className="bg-white border-b" style={{ borderColor: '#E2E8F0' }}>
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_schedule-clean-3/artifacts/ps55wz7g_6CC27561-FF2C-4C06-AEF1-E32A8E83E69A.png"
                alt="Radius Cleaning Company"
                className="h-16 w-auto object-contain"
              />
              <span className="text-lg font-medium px-3 py-1 rounded-full" style={{ background: '#F3F4F6', color: '#475569' }}>
                Admin
              </span>
            </div>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-gray-100 transition-colors"
              style={{ color: '#475569' }}
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-8 py-12">
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <StatCard icon={Users} label="Total Bookings" value={stats.total_bookings} color="#0284C7" />
            <StatCard icon={CheckCircle} label="Paid Bookings" value={stats.paid_bookings} color="#10B981" />
            <StatCard icon={Clock} label="Pending" value={stats.pending_bookings} color="#38BDF8" />
            <StatCard icon={DollarSign} label="Total Revenue" value={`$${stats.total_revenue.toFixed(2)}`} color="#0284C7" />
          </div>
        )}

        {qbStatus && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-8 mb-8"
            style={{ border: '1px solid #E2E8F0' }}
          >
            <div className="flex items-start justify-between gap-6">
              <div className="flex items-start gap-4 flex-1">
                <Database className="w-8 h-8 mt-1" style={{ color: qbStatus.connected ? '#10B981' : '#38BDF8' }} />
                <div>
                  <h3 className="text-xl font-medium mb-2" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
                    QuickBooks Integration
                  </h3>
                  {!qbStatus.credentials_configured ? (
                    <div>
                      <p className="text-base mb-4" style={{ color: '#475569' }}>
                        <AlertCircle className="inline w-4 h-4 mr-2" style={{ color: '#38BDF8' }} />
                        QuickBooks credentials not configured
                      </p>
                      <div className="text-sm space-y-1" style={{ color: '#475569' }}>
                        <p className="font-medium">Setup Instructions:</p>
                        <p>1. {qbStatus.setup_instructions.step1}</p>
                        <p>2. {qbStatus.setup_instructions.step2}</p>
                        <p>3. {qbStatus.setup_instructions.step3}</p>
                        <p>4. {qbStatus.setup_instructions.step4}</p>
                        <p>5. {qbStatus.setup_instructions.step5}</p>
                      </div>
                    </div>
                  ) : qbStatus.connected ? (
                    <div>
                      <p className="text-base mb-2" style={{ color: '#10B981' }}>
                        <CheckCircle className="inline w-4 h-4 mr-2" />
                        Connected to QuickBooks ({qbStatus.environment})
                      </p>
                      <p className="text-sm" style={{ color: '#475569' }}>
                        Realm ID: {qbStatus.realm_id}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-base mb-2" style={{ color: '#38BDF8' }}>
                        Credentials configured ({qbStatus.environment}). Click Connect to authorize.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              {qbStatus.credentials_configured && (
                <div>
                  {qbStatus.connected ? (
                    <button
                      data-testid="qb-disconnect-btn"
                      onClick={handleDisconnectQuickBooks}
                      className="px-6 py-2 rounded-full font-medium hover:opacity-90 transition-opacity"
                      style={{ background: '#FEE2E2', color: '#991B1B' }}
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button
                      data-testid="qb-connect-btn"
                      onClick={handleConnectQuickBooks}
                      className="px-6 py-2 rounded-full font-medium hover:opacity-90 transition-opacity"
                      style={{ background: '#0284C7', color: '#FFFFFF' }}
                    >
                      Connect QuickBooks
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b" style={{ borderColor: '#E2E8F0' }}>
          {[
            { id: 'bookings', label: 'Bookings', icon: Users, count: bookings.length },
            { id: 'applications', label: 'Job Applications', icon: Briefcase, count: applications.length },
            { id: 'pricing', label: 'Pricing', icon: Tag, count: null }
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 px-6 py-3 font-medium transition-colors"
              style={{
                color: activeTab === tab.id ? '#0284C7' : '#475569',
                borderBottom: activeTab === tab.id ? '2px solid #0284C7' : '2px solid transparent',
                marginBottom: '-1px'
              }}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== null && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{
                    background: activeTab === tab.id ? '#0284C7' : '#E2E8F0',
                    color: activeTab === tab.id ? '#FFFFFF' : '#475569'
                  }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'pricing' && pricing && (
          <div className="bg-white rounded-2xl p-8" style={{ border: '1px solid #E2E8F0' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-medium" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
                Pricing Configuration
              </h2>
              {!editingPricing ? (
                <button
                  data-testid="edit-pricing-btn"
                  onClick={startEditPricing}
                  className="flex items-center gap-2 px-4 py-2 rounded-full hover:opacity-90 transition-opacity"
                  style={{ background: '#0284C7', color: '#FFFFFF' }}
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Prices
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    data-testid="cancel-pricing-btn"
                    onClick={cancelEditPricing}
                    className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-gray-100 transition-colors"
                    style={{ color: '#475569' }}
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    data-testid="save-pricing-btn"
                    onClick={savePricing}
                    className="flex items-center gap-2 px-4 py-2 rounded-full hover:opacity-90 transition-opacity"
                    style={{ background: '#10B981', color: '#FFFFFF' }}
                  >
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-sm font-bold tracking-[0.15em] uppercase mb-4" style={{ color: '#475569' }}>
                  Service Base Prices
                </h3>
                <div className="space-y-3">
                  {Object.entries(editingPricing ? pricingDraft.service_base_prices : pricing.service_base_prices).map(([service, price]) => (
                    <div key={service} className="flex items-center justify-between gap-4 p-4 rounded-xl" style={{ background: '#F3F4F6' }}>
                      <span className="font-medium" style={{ color: '#0F172A' }}>{service}</span>
                      {editingPricing ? (
                        <div className="flex items-center gap-1">
                          <span style={{ color: '#475569' }}>$</span>
                          <input
                            data-testid={`price-input-${service.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                            type="number"
                            step="0.01"
                            value={pricingDraft.service_base_prices[service]}
                            onChange={(e) => setPricingDraft({
                              ...pricingDraft,
                              service_base_prices: {
                                ...pricingDraft.service_base_prices,
                                [service]: parseFloat(e.target.value) || 0
                              }
                            })}
                            className="w-24 px-3 py-1 rounded-lg text-right"
                            style={{ border: '1px solid #E2E8F0' }}
                          />
                        </div>
                      ) : (
                        <span className="text-lg font-medium" style={{ color: '#0284C7' }}>${price.toFixed(2)}</span>
                      )}
                    </div>
                  ))}
                </div>

                <h3 className="text-sm font-bold tracking-[0.15em] uppercase mt-8 mb-4" style={{ color: '#475569' }}>
                  Per-Room Charges <span className="text-xs font-normal ml-2" style={{ color: '#94A3B8' }}>Standard | Deep Cleaning</span>
                </h3>
                <div className="space-y-3">
                  {Object.entries(editingPricing ? pricingDraft.room_prices : pricing.room_prices).map(([room, tiers]) => (
                    <div key={room} className="flex items-center justify-between gap-4 p-4 rounded-xl" style={{ background: '#F3F4F6' }}>
                      <span className="font-medium" style={{ color: '#0F172A' }}>{room}</span>
                      {editingPricing ? (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs" style={{ color: '#475569' }}>Std $</span>
                            <input
                              data-testid={`price-room-std-${room.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                              type="number"
                              step="0.01"
                              value={pricingDraft.room_prices[room].standard}
                              onChange={(e) => setPricingDraft({
                                ...pricingDraft,
                                room_prices: {
                                  ...pricingDraft.room_prices,
                                  [room]: { ...pricingDraft.room_prices[room], standard: parseFloat(e.target.value) || 0 }
                                }
                              })}
                              className="w-20 px-2 py-1 rounded-lg text-right"
                              style={{ border: '1px solid #E2E8F0' }}
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs" style={{ color: '#475569' }}>Deep $</span>
                            <input
                              data-testid={`price-room-deep-${room.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                              type="number"
                              step="0.01"
                              value={pricingDraft.room_prices[room].deep}
                              onChange={(e) => setPricingDraft({
                                ...pricingDraft,
                                room_prices: {
                                  ...pricingDraft.room_prices,
                                  [room]: { ...pricingDraft.room_prices[room], deep: parseFloat(e.target.value) || 0 }
                                }
                              })}
                              className="w-20 px-2 py-1 rounded-lg text-right"
                              style={{ border: '1px solid #E2E8F0' }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-lg font-medium" style={{ color: '#0284C7' }}>
                          ${(tiers.standard ?? tiers).toFixed(2)} <span className="text-sm" style={{ color: '#94A3B8' }}>|</span> ${(tiers.deep ?? tiers).toFixed(2)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold tracking-[0.15em] uppercase mb-4" style={{ color: '#475569' }}>
                  Extra Services Prices <span className="text-xs font-normal ml-2" style={{ color: '#94A3B8' }}>Standard | Deep</span>
                </h3>
                <div className="space-y-3">
                  {Object.entries(editingPricing ? pricingDraft.extra_services_prices : pricing.extra_services_prices).map(([service, tiers]) => (
                    <div key={service} className="flex items-center justify-between gap-4 p-4 rounded-xl" style={{ background: '#F3F4F6' }}>
                      <span className="font-medium" style={{ color: '#0F172A' }}>{service}</span>
                      {editingPricing ? (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs" style={{ color: '#475569' }}>Std $</span>
                            <input
                              data-testid={`price-extra-std-${service.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                              type="number"
                              step="0.01"
                              value={pricingDraft.extra_services_prices[service].standard}
                              onChange={(e) => setPricingDraft({
                                ...pricingDraft,
                                extra_services_prices: {
                                  ...pricingDraft.extra_services_prices,
                                  [service]: { ...pricingDraft.extra_services_prices[service], standard: parseFloat(e.target.value) || 0 }
                                }
                              })}
                              className="w-20 px-2 py-1 rounded-lg text-right"
                              style={{ border: '1px solid #E2E8F0' }}
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs" style={{ color: '#475569' }}>Deep $</span>
                            <input
                              data-testid={`price-extra-deep-${service.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                              type="number"
                              step="0.01"
                              value={pricingDraft.extra_services_prices[service].deep}
                              onChange={(e) => setPricingDraft({
                                ...pricingDraft,
                                extra_services_prices: {
                                  ...pricingDraft.extra_services_prices,
                                  [service]: { ...pricingDraft.extra_services_prices[service], deep: parseFloat(e.target.value) || 0 }
                                }
                              })}
                              className="w-20 px-2 py-1 rounded-lg text-right"
                              style={{ border: '1px solid #E2E8F0' }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-lg font-medium" style={{ color: '#0284C7' }}>
                          ${(tiers.standard ?? tiers).toFixed(2)} <span className="text-sm" style={{ color: '#94A3B8' }}>|</span> ${(tiers.deep ?? tiers).toFixed(2)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'applications' && (
          <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #E2E8F0' }}>
            <div className="p-8 border-b" style={{ borderColor: '#E2E8F0' }}>
              <h2 className="text-2xl font-medium" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
                Job Applications ({applications.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ background: '#F3F4F6' }}>
                  <tr>
                    <th className="text-left px-6 py-4 text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#475569' }}>Applicant</th>
                    <th className="text-left px-6 py-4 text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#475569' }}>Position</th>
                    <th className="text-left px-6 py-4 text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#475569' }}>Experience</th>
                    <th className="text-left px-6 py-4 text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#475569' }}>Available</th>
                    <th className="text-left px-6 py-4 text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#475569' }}>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-12" style={{ color: '#475569' }}>
                        No applications yet
                      </td>
                    </tr>
                  ) : (
                    applications.map((app) => (
                      <tr key={app.id} className="border-t" style={{ borderColor: '#E2E8F0' }} data-testid={`application-row-${app.id}`}>
                        <td className="px-6 py-4">
                          <div className="font-medium" style={{ color: '#0F172A' }}>{app.full_name}</div>
                          <div className="text-sm" style={{ color: '#475569' }}>
                            <a href={`mailto:${app.email}`} style={{ color: '#0284C7' }}>{app.email}</a>
                          </div>
                          <div className="text-sm" style={{ color: '#475569' }}>{app.phone}</div>
                        </td>
                        <td className="px-6 py-4" style={{ color: '#0F172A' }}>{app.position}</td>
                        <td className="px-6 py-4" style={{ color: '#475569' }}>{app.experience_years || '—'}</td>
                        <td className="px-6 py-4" style={{ color: '#475569' }}>{app.available_start_date || '—'}</td>
                        <td className="px-6 py-4 text-sm" style={{ color: '#475569' }}>
                          {new Date(app.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'bookings' && (
        <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #E2E8F0' }}>
          <div className="p-8 border-b flex items-center justify-between" style={{ borderColor: '#E2E8F0' }}>
            <h2 className="text-2xl font-medium" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
              All Bookings ({bookings.length})
            </h2>
            <button
              data-testid="refresh-bookings-btn"
              onClick={() => loadData(password)}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-gray-100 transition-colors"
              style={{ color: '#475569' }}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ background: '#F3F4F6' }}>
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#475569' }}>Customer</th>
                  <th className="text-left px-6 py-4 text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#475569' }}>Service</th>
                  <th className="text-left px-6 py-4 text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#475569' }}>Date/Time</th>
                  <th className="text-left px-6 py-4 text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#475569' }}>Amount</th>
                  <th className="text-left px-6 py-4 text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#475569' }}>Payment</th>
                  <th className="text-left px-6 py-4 text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#475569' }}>QuickBooks</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-12" style={{ color: '#475569' }}>
                      No bookings yet
                    </td>
                  </tr>
                ) : (
                  bookings.map((booking) => (
                    <tr key={booking.id} className="border-t hover:bg-gray-50" style={{ borderColor: '#E2E8F0' }} data-testid={`booking-row-${booking.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium" style={{ color: '#0F172A' }}>{booking.customer_name}</div>
                        <div className="text-sm" style={{ color: '#475569' }}>{booking.customer_email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div style={{ color: '#0F172A' }}>{booking.service_type}</div>
                        <div className="text-sm" style={{ color: '#475569' }}>
                          {booking.bedrooms} BR, {booking.bathrooms} BA
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div style={{ color: '#0F172A' }}>{booking.scheduled_date}</div>
                        <div className="text-sm" style={{ color: '#475569' }}>{booking.scheduled_time}</div>
                      </td>
                      <td className="px-6 py-4 font-medium" style={{ color: '#0F172A' }}>
                        ${booking.total_amount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            background: booking.payment_status === 'paid' ? '#D1FAE5' : '#DBEAFE',
                            color: booking.payment_status === 'paid' ? '#065F46' : '#1E40AF'
                          }}
                        >
                          {booking.payment_status === 'paid' ? 'Paid' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {booking.synced_to_quickbooks ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: '#10B981' }}>
                            <CheckCircle className="w-4 h-4" />
                            Synced
                          </span>
                        ) : (
                          <button
                            data-testid={`sync-booking-btn-${booking.id}`}
                            onClick={() => handleSyncBooking(booking.id)}
                            disabled={syncing[booking.id] || booking.payment_status !== 'paid'}
                            title={booking.payment_status !== 'paid' ? 'Available after payment is completed' : 'Sync this booking to QuickBooks'}
                            className="text-xs font-medium px-3 py-1 rounded-full transition-all disabled:cursor-not-allowed"
                            style={{
                              background: booking.payment_status === 'paid' ? '#0284C7' : '#E2E8F0',
                              color: booking.payment_status === 'paid' ? '#FFFFFF' : '#94A3B8'
                            }}
                          >
                            {syncing[booking.id] ? 'Syncing...' : booking.payment_status === 'paid' ? 'Sync to QB' : 'Awaiting Payment'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-2xl p-8"
    style={{ border: '1px solid #E2E8F0' }}
  >
    <Icon className="w-8 h-8 mb-4" style={{ color }} />
    <div className="text-3xl font-light mb-2" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
      {value}
    </div>
    <div className="text-sm" style={{ color: '#475569' }}>
      {label}
    </div>
  </motion.div>
);

export default AdminDashboard;
